/* eslint-disable max-len */
import { useMemo, useEffect } from 'react'

import * as React from 'react'
import { trackInternalEvent } from '@rio/tracking'
import {
  useTimeLeftFormatter,
  ResumeCritique,
  ResumeCritiqueStatuses as STATUSES,
} from 'builder/modules/resumeReview'
import { ButtonSize, ButtonTheme } from 'builder/components/Button'
import { Icon20, Icon24 } from 'builder/components/Icon'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { IFRAME_PARAMS } from 'builder/modules/resumeCritiqueReview/constants'
import { i18n as I18n } from 'builder/utils/i18n'

import {
  StatusContainer,
  StatusVisual,
  StatusIconWrapper,
  StatusIcon,
  StatusPhoto,
  StatusInner,
  StatusContent,
  StatusTitle,
  StatusText,
  StatusButton,
  StatusButtonIcon,
} from './styles'

interface ResumeReviewStatusUiProps extends React.HTMLAttributes<HTMLDivElement> {
  critique: ResumeCritique
  onClose: () => void
}

export const ResumeReviewStatusUi = ({ critique, onClose, ...rest }: ResumeReviewStatusUiProps) => {
  const { status } = critique
  const timeLeft = useTimeLeftFormatter(critique)
  const { isTablet } = useMediaQueries()

  useEffect(() => {
    trackInternalEvent('see_resume_critique_banner', {
      source: 'dashboard',
      status,
    })
  }, [status])

  const title = useMemo(() => {
    if (status === STATUSES.failed)
      return I18n.t('builder.panel.critique_review_failed_state.title')
    if (status === STATUSES.finished)
      return I18n.t('builder.panel.critique_review_success_state.title')
    return I18n.t('builder.panel.critique_review_pending_state.title')
  }, [status])

  const text = useMemo(() => {
    if (status === STATUSES.failed) {
      return I18n.t('builder.panel.critique_review_failed_state.text')
    }
    if (status === STATUSES.finished) {
      return I18n.t('builder.panel.critique_review_success_state.text', {
        critiqueName: critique.reviewer.name,
      })
    }
    return I18n.t('builder.panel.critique_review_pending_state.text', {
      timeLeft: timeLeft,
    })
  }, [status, timeLeft, critique.reviewer.name])

  const onClickStatusIconWrapper = () => {
    if (status === STATUSES.failed) {
      trackInternalEvent('click_resume_critique_error_icon', {
        section: 'banner',
      })
    }
  }

  const onClickStatusFailedButton = () => {
    onClose()
    trackInternalEvent('close_resume_critique_error')
  }

  const onClickStatusButtonFinished = (url: string) => {
    trackInternalEvent('click_read_resume_critique_button', {
      section: 'banner',
      source: 'dashboard',
    })
    window.open(`${url}${IFRAME_PARAMS}`)
  }

  return (
    <StatusContainer {...rest} status={status}>
      <StatusVisual>
        {status === STATUSES.pending && !isTablet && (
          <StatusPhoto src={require('images/builder/panel/resume-review-status/photo-2.jpg')} />
        )}
        {status === STATUSES.pending && (
          <StatusPhoto src={require('images/builder/panel/resume-review-status/photo-1.jpg')} />
        )}
        {status === STATUSES.finished && <StatusPhoto src={critique.reviewer.photoUrl} />}

        <StatusIconWrapper onClick={onClickStatusIconWrapper} status={status}>
          <StatusIcon as={status === STATUSES.failed ? Icon24.Close : Icon20.Star} />
        </StatusIconWrapper>
      </StatusVisual>

      <StatusInner>
        <StatusContent>
          <StatusTitle>{title}</StatusTitle>
          <StatusText>{text}</StatusText>
        </StatusContent>

        {/* TODO: i18n */}
        {status === STATUSES.pending && (
          <StatusButton size={ButtonSize.small} isDisabled>
            {I18n.t('builder.panel.critique_review_pending_state.button', { timeLeft: timeLeft })}
          </StatusButton>
        )}

        {/* TODO: i18n */}
        {status === STATUSES.finished && (
          <StatusButton
            status={status}
            onClick={() => onClickStatusButtonFinished(critique.iframeUrl)}
            size={ButtonSize.small}
          >
            {I18n.t('builder.panel.critique_review_success_state.button')} <StatusButtonIcon />
          </StatusButton>
        )}

        {/* TODO: i18n */}
        {status === STATUSES.failed && (
          <StatusButton
            onClick={onClickStatusFailedButton}
            theme={ButtonTheme.destructive}
            size={ButtonSize.small}
          >
            {I18n.t('builder.panel.critique_review_failed_state.button')}
          </StatusButton>
        )}
      </StatusInner>
    </StatusContainer>
  )
}
